import { motion } from "framer-motion";
import { Receipt, CreditCard, Users, BellRing, Send, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { Reveal, SectionHead } from "./Section";
import { BrowserFrame, PhoneFrame, PhoneShell, MockShell } from "./Frames";
import { InvoicesBody, PayScreen } from "./screens/InvoiceScreens";
import { EmailSetupButton, MailLine } from "./EmailCta";

const POINTS = [
  [Receipt, "Invoice a finished job in a tap", "Priced from clocked hours, a set price per job type or your own lines, with GST."],
  [CreditCard, "Clients pay by card or bank", "Every invoice is emailed as a PDF with a “View and pay” link to your own Stripe account or bank details."],
  [Users, "See who owes you", "Owing and overdue balances on every client, with overdue amounts in red."],
  [BellRing, "Know the moment you’re paid", "An email or phone alert as soon as a client pays by card."],
  [Send, "Chase overdue in one go", "One friendly reminder per client, plus PDF statements whenever you need them."],
  [FileSpreadsheet, "Ready for your accountant", "Export your invoices to CSV for any date range."],
];

const Paid = () => (
  <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: [0, -6, 0] }} viewport={{ once: true }}
    transition={{ opacity: { delay: 0.6 }, y: { delay: 0.6, duration: 5, repeat: Infinity, ease: "easeInOut" } }}
    className="absolute hidden md:flex -top-5 left-8 items-center gap-2 rounded-xl bg-[#0B1120] text-white px-3 py-2 text-xs font-semibold shadow-2xl">
    <CheckCircle2 className="w-4 h-4 text-emerald-400" /> INV-0046 paid by card · $2,860.00
  </motion.div>
);

export const InvoicesNew = ({ onStart }) => (
  <section id="invoices" className="scroll-mt-16 bg-white py-24 lg:py-32 overflow-hidden" data-testid="home-invoices">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.15fr] gap-14 lg:gap-16 items-center">
      <div>
        <SectionHead num="NEW" eyebrow="Client invoices" title="Invoice the job the moment it’s done."
          sub="Invoices are now built into Cross Jobs. Turn completed jobs into invoices, email them with a pay link and see exactly who owes you — without another app." />
        <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-6">
          {POINTS.map(([Icon, t, p], i) => (
            <Reveal key={t} delay={0.05 * i}>
              <div className="flex gap-4" data-testid={`home-invoices-point-${i}`}>
                <span className="w-10 h-10 shrink-0 rounded-xl bg-[#1E3A5F] text-white grid place-items-center"><Icon className="w-5 h-5" /></span>
                <div><h3 className="font-display font-bold text-base sm:text-lg leading-snug">{t}</h3><p className="mt-1 text-sm text-slate-600">{p}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
          <EmailSetupButton testid="home-invoices-email-btn" />
          <button onClick={onStart} data-testid="home-invoices-start-btn" className="font-semibold text-[#1E3A5F] underline-offset-4 hover:underline hover:text-[#EA580C] transition-colors">or try it free for 30 days</button>
        </div>
        <div className="mt-4"><MailLine testid="home-invoices-mail" dark={false} /></div>
      </div>
      <Reveal delay={0.12} className="relative lg:pr-10 pb-10">
        <BrowserFrame title="Cross Jobs · Invoices" testid="home-invoices-browser"><MockShell tab="Invoices"><InvoicesBody /></MockShell></BrowserFrame>
        <div className="hidden sm:block absolute -bottom-8 -right-3 lg:-right-8 w-[150px] lg:w-[160px]" data-testid="home-invoices-pay-phone"><PhoneFrame><PhoneShell title="Your invoice"><PayScreen /></PhoneShell></PhoneFrame></div>
        <Paid />
      </Reveal>
    </div>
  </section>
);
